// File sync module — communicates with Tauri Rust backend for .tex file I/O
import type { MathZone, FileSyncState } from "../triggers/types";

// We'll use Tauri's invoke API when running in Tauri
// For development in browser, we use a mock
let invoke: ((cmd: string, args?: Record<string, unknown>) => Promise<unknown>) | null = null;

async function getInvoke() {
    if (invoke) return invoke;
    try {
        const tauri = await import("@tauri-apps/api/core");
        invoke = tauri.invoke;
        return invoke;
    } catch {
        // Running outside Tauri (dev in browser), use mock
        if (import.meta.env.DEV) console.warn("Tauri API not available — using mock file sync");
        return null;
    }
}

/**
 * Open a .tex file and parse its math zones
 */
export async function openTexFile(filePath: string): Promise<MathZone[]> {
    const inv = await getInvoke();
    if (!inv) {
        return mockParseMathZones(filePath);
    }
    const zones = (await inv("open_tex_file", { path: filePath })) as MathZone[];
    return zones;
}

/**
 * Update a specific math zone in the file
 */
export async function updateMathZone(
    filePath: string,
    zoneId: string,
    newLatex: string
): Promise<boolean> {
    const inv = await getInvoke();
    if (!inv) {
        if (import.meta.env.DEV) console.log(`[mock] updateMathZone: ${zoneId} => ${newLatex}`);
        return true;
    }
    return (await inv("update_math_zone", {
        path: filePath,
        zoneId,
        latex: newLatex,
    })) as boolean;
}

/**
 * Save the entire file
 */
export async function saveFile(filePath: string): Promise<boolean> {
    const inv = await getInvoke();
    if (!inv) {
        if (import.meta.env.DEV) console.log(`[mock] saveFile: ${filePath}`);
        return true;
    }
    return (await inv("save_file", { path: filePath })) as boolean;
}

/**
 * Get the full file contents
 */
export async function readFileContents(filePath: string): Promise<string> {
    const inv = await getInvoke();
    if (!inv) {
        return mockFileContents;
    }
    return (await inv("read_file_contents", { path: filePath })) as string;
}

// ============================================================
// Mock data for browser development
// ============================================================
const mockFileContents = `\\documentclass{article}
\\usepackage{amsmath}
\\begin{document}

The quadratic formula is
\\[
  x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}
\\]

Euler's identity: $e^{i\\pi} + 1 = 0$

\\begin{equation}
  \\int_0^\\infty e^{-x^2} \\, dx = \\frac{\\sqrt{\\pi}}{2}
\\end{equation}

\\begin{align*}
  \\nabla \\cdot \\mathbf{E} &= \\frac{\\rho}{\\epsilon_0} \\\\
  \\nabla \\cdot \\mathbf{B} &= 0
\\end{align*}

\\end{document}`;

function mockParseMathZones(_filePath: string): MathZone[] {
    return [
        {
            id: "zone-1",
            latex: "x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}",
            startOffset: 100,
            endOffset: 155,
            envType: "display",
            line: 6,
        },
        {
            id: "zone-2",
            latex: "e^{i\\pi} + 1 = 0",
            startOffset: 160,
            endOffset: 180,
            envType: "inline",
            line: 10,
        },
        {
            id: "zone-3",
            latex: "\\int_0^\\infty e^{-x^2} \\, dx = \\frac{\\sqrt{\\pi}}{2}",
            startOffset: 200,
            endOffset: 260,
            envType: "equation",
            line: 12,
        },
        {
            id: "zone-4",
            latex: "\\nabla \\cdot \\mathbf{E} &= \\frac{\\rho}{\\epsilon_0} \\\\\n  \\nabla \\cdot \\mathbf{B} &= 0",
            startOffset: 280,
            endOffset: 370,
            envType: "align*",
            line: 16,
        },
    ];
}

/**
 * Create a new FileSyncState
 */
export function createFileSyncState(): FileSyncState {
    return {
        filePath: null,
        mathZones: [],
        activeZoneId: null,
        isDirty: false,
        lastSyncTime: Date.now(),
    };
}

/**
 * Compile LaTeX content to PDF using system's pdflatex
 * Returns the path to the generated PDF
 */
export async function compileToPdf(latexContent: string): Promise<string> {
    const inv = await getInvoke();
    if (!inv) {
        if (import.meta.env.DEV) console.log("[mock] compileToPdf called");
        throw new Error("PDF compilation requires the Tauri desktop app");
    }
    return (await inv("compile_to_pdf", { latexContent })) as string;
}
